/**
 * Depositor Graph Signing Service
 *
 * Signs the depositor's own graph transactions (Payout, NoPayout per challenger)
 * for the depositor-as-claimer flow.
 *
 * Both PSBTs are constructed locally from authoritative on-chain connector
 * parameters and the VP-advertised transaction hexes (which are themselves
 * cross-checked against on-chain or protocol-defined sinks). Building PSBTs
 * locally is essential: every field that enters the Taproot sighash
 * (witnessUtxo, tapLeafScript, controlBlock, tapInternalKey) must come from
 * trusted sources, otherwise a malicious VP could substitute metadata that
 * makes the depositor's signature valid for a different spend.
 *
 * Transaction counts: 1 Payout + N NoPayout = 1 + N total PSBTs.
 *
 * @see btc-vault docs/pegin.md - "Automatic Graph Creation & Presigning"
 * @see btc-vault crates/vault/src/transactions/nopayout.rs - NoPayout structure
 */

import { type Network } from "@babylonlabs-io/babylon-tbv-rust-wasm";
import { Transaction } from "bitcoinjs-lib";

import type {
  BitcoinWallet,
  SignPsbtOptions,
} from "../../../../shared/wallets/interfaces";
import type {
  DepositorAsClaimerPresignatures,
  DepositorGraphTransactions,
  DepositorPreSigsPerChallenger,
  PresignDataPerChallenger,
} from "../../clients/vault-provider/types";
import { signPsbtsWithFallback } from "../../managers/pegin/signPsbtsWithFallback";
import { deriveLocalChallengers } from "../../primitives/challengers";
import {
  assertPsbtUnsignedTxMatches,
  type AssertPsbtUnsignedTxMatchesParams,
} from "../../primitives/psbt/assertPsbtUnsignedTxMatches";
import {
  assertNoPayoutOutputMatchesChallenger,
  buildNoPayoutPsbt,
} from "../../primitives/psbt/noPayout";
import {
  buildPayoutPsbt,
  extractPayoutSignature,
} from "../../primitives/psbt/payout";
import { assertScriptPathSchnorrSignature } from "../../primitives/psbt/verifyScriptPathSchnorrSignature";
import {
  stripHexPrefix,
  uint8ArrayToHex,
  validateWalletPubkey,
} from "../../primitives/utils/bitcoin";
import { createTaprootScriptPathSignOptions } from "../../utils/signing";

/**
 * The depositor signs exactly one input (index 0) per payout/nopayout PSBT.
 * Used to construct SignPsbtOptions for wallet.signPsbt(). PSBTs may carry
 * additional inputs (the payout PSBT includes the assert prevout; the nopayout
 * PSBT includes the two ChallengeAssert prevouts) so the Taproot SIGHASH_DEFAULT
 * sighash commits to all prevouts, but those inputs are not signed by the
 * depositor.
 */
const DEPOSITOR_SIGNED_INPUT_COUNT = 1;

/**
 * commissionBps placeholder for the depositor-as-claimer path — `buildPayoutPsbt`
 * only consults it under the VP-claimer role, so any in-range value is inert.
 */
const DEPOSITOR_PATH_UNUSED_COMMISSION_BPS = 1;

/** Tracks which indices in the flat PSBT array belong to which challenger */
interface ChallengerEntry {
  challengerPubkey: string;
  noPayoutIdx: number;
}

/** Result of the collect phase - flat PSBT array with index mapping */
interface CollectedDepositorGraphPsbts {
  psbtHexes: string[];
  signOptions: SignPsbtOptions[];
  challengerEntries: ChallengerEntry[];
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Reject VP-supplied `challenger_presign_data` whose pubkey set does not
 * exactly equal `localChallengers ∪ universalChallengers`.
 *
 * The daemon's `challenger_presign_data` contains one entry per challenger
 * in `Challengers::all_sorted() = local + universal` (per
 * btc-vault `crates/vault/src/tx_graph/graph.rs:438-458`). For the
 * depositor-as-claimer flow this is `VKs + UCs`.
 *
 * Threat model: a malicious or buggy VP could omit, duplicate, or inject
 * unrelated entries. Missing entries → depositor activates with incomplete
 * recovery material (omitted challenger later becomes unenforceable).
 * Duplicates or extras → wallet signs PSBTs for challengers the protocol
 * doesn't recognize, handing the VP signatures it shouldn't have.
 */
function assertChallengerSetMatchesExpected(
  challengerPresignData: PresignDataPerChallenger[],
  localChallengers: string[],
  universalChallengerBtcPubkeys: string[],
): void {
  const universal = universalChallengerBtcPubkeys.map((k) =>
    stripHexPrefix(k).toLowerCase(),
  );
  // Protocol guarantee: local and universal sets are disjoint. Reject
  // overlap so the depositor doesn't sign for an ambiguous challenger role.
  const overlap = localChallengers.filter((k) => universal.includes(k));
  if (overlap.length > 0) {
    throw new Error(
      `Cannot validate challenger set: vault keepers and universal challengers overlap (${overlap.join(", ")})`,
    );
  }
  const expected = [...localChallengers, ...universal];

  const suppliedList = challengerPresignData.map((c) =>
    stripHexPrefix(c.challenger_pubkey).toLowerCase(),
  );
  const suppliedSet = new Set(suppliedList);
  if (suppliedSet.size !== suppliedList.length) {
    throw new Error(
      "Depositor graph contains duplicate challenger entries in challenger_presign_data",
    );
  }
  const expectedSet = new Set(expected);
  const missing = expected.filter((c) => !suppliedSet.has(c));
  const extra = suppliedList.filter((c) => !expectedSet.has(c));
  if (missing.length > 0 || extra.length > 0) {
    throw new Error(
      `Depositor graph challenger set does not match expected (local ∪ universal)` +
        (missing.length > 0 ? ` (missing: ${missing.join(", ")})` : "") +
        (extra.length > 0 ? ` (unexpected: ${extra.join(", ")})` : ""),
    );
  }
}

/**
 * Read the txid that the given input references in the unsigned tx, in display
 * (big-endian) hex order. bitcoinjs-lib stores `input.hash` in internal
 * little-endian byte order, which is the reverse of how txids are normally
 * displayed.
 */
function readInputTxid(tx: Transaction, inputIndex: number): string {
  const input = tx.ins[inputIndex];
  return uint8ArrayToHex(new Uint8Array(input.hash).slice().reverse());
}

/**
 * Verify the noPayout transaction's input at `inputIndex` references the
 * given parent transaction at vout 0 (per nopayout.rs the layout is fixed:
 * Assert:0, ChallengeAssertX:0, ChallengeAssertY:0).
 */
function assertInputReferencesParent(
  noPayoutTx: Transaction,
  inputIndex: number,
  parentTx: Transaction,
  parentLabel: string,
  challengerPubkey: string,
): void {
  const input = noPayoutTx.ins[inputIndex];
  if (input.index !== 0) {
    throw new Error(
      `NoPayout (challenger ${challengerPubkey}) input ${inputIndex} expected to spend ${parentLabel} vout 0, got vout ${input.index}`,
    );
  }
  const parentTxid = parentTx.getId();
  const inputTxid = readInputTxid(noPayoutTx, inputIndex);
  if (inputTxid !== parentTxid) {
    throw new Error(
      `NoPayout (challenger ${challengerPubkey}) input ${inputIndex} does not reference ${parentLabel} (expected txid ${parentTxid}, got ${inputTxid})`,
    );
  }
}

// ============================================================================
// Collect phase
// ============================================================================

/**
 * Build the depositor's payout PSBT and per-challenger NoPayout PSBTs locally
 * from authoritative connector params.
 *
 * Layout of returned arrays: [Payout, NoPayout_0, NoPayout_1, ...]
 */
async function collectDepositorGraphPsbts(
  depositorGraph: DepositorGraphTransactions,
  walletPublicKey: string,
  ctx: DepositorGraphSigningContext,
): Promise<CollectedDepositorGraphPsbts> {
  const psbtHexes: string[] = [];
  const signOptions: SignPsbtOptions[] = [];
  const challengerEntries: ChallengerEntry[] = [];

  // 1. Fail-fast on a malformed VP response BEFORE doing any PSBT-build
  //    work that would be wasted if the challenger set is wrong.
  const localChallengers = deriveLocalChallengers({
    claimerBtcPubkey: ctx.depositorBtcPubkey,
    depositorBtcPubkey: ctx.depositorBtcPubkey,
    vaultProviderBtcPubkey: ctx.vaultProviderBtcPubkey,
    vaultKeeperBtcPubkeys: ctx.vaultKeeperBtcPubkeys,
  });
  assertChallengerSetMatchesExpected(
    depositorGraph.challenger_presign_data,
    localChallengers,
    ctx.universalChallengerBtcPubkeys,
  );

  // 2. Build the payout PSBT locally — every sighash-relevant field is
  //    derived from trusted on-chain connector params, not from the VP.
  //    buildPayoutPsbt also runs the per-role output validation.
  const builtPayout = await buildPayoutPsbt({
    vaultCoreVersion: ctx.vaultCoreVersion,
    vkClaimerPayoutScriptPubKeys: ctx.vkClaimerPayoutScriptPubKeys,
    vpCommissionScriptPubKey: ctx.vpCommissionScriptPubKey,
    payoutTxHex: depositorGraph.payout_tx.tx_hex,
    peginTxHex: ctx.peginTxHex,
    assertTxHex: depositorGraph.assert_tx.tx_hex,
    timelockAssert: ctx.timelockAssert,
    depositorBtcPubkey: ctx.depositorBtcPubkey,
    vaultProviderBtcPubkey: ctx.vaultProviderBtcPubkey,
    vaultKeeperBtcPubkeys: ctx.vaultKeeperBtcPubkeys,
    universalChallengerBtcPubkeys: ctx.universalChallengerBtcPubkeys,
    timelockPegin: ctx.timelockPegin,
    network: ctx.network,
    claimerBtcPubkey: ctx.depositorBtcPubkey,
    registeredPayoutScriptPubKey: ctx.registeredPayoutScriptPubKey,
    commissionBps: DEPOSITOR_PATH_UNUSED_COMMISSION_BPS,
    protocolFeeRate: ctx.protocolFeeRate,
    councilMembers: ctx.councilMembers,
    councilQuorum: ctx.councilQuorum,
  });
  psbtHexes.push(builtPayout.psbtHex);
  signOptions.push(
    createTaprootScriptPathSignOptions(
      walletPublicKey,
      DEPOSITOR_SIGNED_INPUT_COUNT,
    ),
  );

  // 3. Per-challenger: build the NoPayout PSBT locally too.
  const claimerPubkey = stripHexPrefix(ctx.depositorBtcPubkey);
  const assertTxParsed = Transaction.fromHex(
    stripHexPrefix(depositorGraph.assert_tx.tx_hex),
  );

  for (const challenger of depositorGraph.challenger_presign_data) {
    const challengerPubkey = stripHexPrefix(challenger.challenger_pubkey);

    const noPayoutIdx = psbtHexes.length;
    const noPayoutHex = await buildLocalNoPayoutPsbt({
      challenger,
      challengerPubkey,
      claimerPubkey,
      localChallengers,
      assertTxParsed,
      ctx,
    });
    psbtHexes.push(noPayoutHex);
    signOptions.push(
      createTaprootScriptPathSignOptions(
        walletPublicKey,
        DEPOSITOR_SIGNED_INPUT_COUNT,
      ),
    );

    challengerEntries.push({
      challengerPubkey,
      noPayoutIdx,
    });
  }

  return { psbtHexes, signOptions, challengerEntries };
}

interface BuildLocalNoPayoutPsbtParams {
  challenger: PresignDataPerChallenger;
  challengerPubkey: string;
  claimerPubkey: string;
  localChallengers: string[];
  assertTxParsed: Transaction;
  ctx: DepositorGraphSigningContext;
}

/**
 * Build a single NoPayout PSBT for one challenger from authoritative
 * inputs. Validates the VP-supplied parent transactions match what the
 * NoPayout transaction commits to via input txids, and asserts the output
 * pays to the protocol-defined challenger sink before returning.
 *
 * NoPayout transaction layout (per
 * btc-vault crates/vault/src/transactions/nopayout.rs):
 * - 3 inputs (fixed order):
 *   - Input 0: Assert tx output 0 (depositor signs - NoPayout path)
 *   - Input 1: ChallengeAssertX tx output 0 (with timelock)
 *   - Input 2: ChallengeAssertY tx output 0 (with timelock)
 * - 1 output: BIP-86 P2TR to the challenger
 */
async function buildLocalNoPayoutPsbt(
  params: BuildLocalNoPayoutPsbtParams,
): Promise<string> {
  const {
    challenger,
    challengerPubkey,
    claimerPubkey,
    localChallengers,
    assertTxParsed,
    ctx,
  } = params;

  // Pin the output sink before doing any sighash-relevant work.
  assertNoPayoutOutputMatchesChallenger(
    challenger.nopayout_tx.tx_hex,
    challengerPubkey,
    ctx.network,
  );

  // Parse the NoPayout tx and the two ChallengeAssert parents.
  const noPayoutTx = Transaction.fromHex(
    stripHexPrefix(challenger.nopayout_tx.tx_hex),
  );
  const challengeAssertXTx = Transaction.fromHex(
    stripHexPrefix(challenger.challenge_assert_x_tx.tx_hex),
  );
  const challengeAssertYTx = Transaction.fromHex(
    stripHexPrefix(challenger.challenge_assert_y_tx.tx_hex),
  );

  if (noPayoutTx.ins.length !== 3) {
    throw new Error(
      `NoPayout (challenger ${challengerPubkey}) must have exactly 3 inputs, got ${noPayoutTx.ins.length}`,
    );
  }

  // Pin every input's parent. Each parent's outs[0] is the authoritative
  // prevout - because we verified the parent's txid matches what the NoPayout
  // tx commits to, the parent cannot be substituted without changing the
  // NoPayout txid.
  assertInputReferencesParent(
    noPayoutTx,
    0,
    assertTxParsed,
    "Assert",
    challengerPubkey,
  );
  assertInputReferencesParent(
    noPayoutTx,
    1,
    challengeAssertXTx,
    "ChallengeAssertX",
    challengerPubkey,
  );
  assertInputReferencesParent(
    noPayoutTx,
    2,
    challengeAssertYTx,
    "ChallengeAssertY",
    challengerPubkey,
  );

  const prevouts = [
    assertTxParsed.outs[0],
    challengeAssertXTx.outs[0],
    challengeAssertYTx.outs[0],
  ].map((out) => ({
    script_pubkey: uint8ArrayToHex(new Uint8Array(out.script)),
    value: out.value,
  }));

  return buildNoPayoutPsbt({
    noPayoutTxHex: challenger.nopayout_tx.tx_hex,
    challengerPubkey,
    prevouts,
    connectorParams: {
      txGraphVersion: ctx.vaultCoreVersion,
      claimer: claimerPubkey,
      localChallengers,
      universalChallengers: ctx.universalChallengerBtcPubkeys,
      timelockAssert: ctx.timelockAssert,
      councilMembers: ctx.councilMembers,
      councilQuorum: ctx.councilQuorum,
    },
  });
}

// ============================================================================
// Extract phase
// ============================================================================

/** A pair of a locally-built PSBT and the wallet-returned PSBT for it. */
type PsbtPair = AssertPsbtUnsignedTxMatchesParams;

/**
 * Extract all signatures from signed PSBTs and assemble into presignatures.
 * Each pair is asserted to encode the same unsigned tx before its signature
 * is extracted — defends against a wallet that returns a signature for a
 * substituted transaction.
 */
function extractDepositorGraphSignatures(
  psbtPairs: PsbtPair[],
  challengerEntries: ChallengerEntry[],
  depositorPubkey: string,
): DepositorAsClaimerPresignatures {
  // Positional invariant: psbtPairs[0] is the payout PSBT; per-challenger
  // nopayouts live at indices recorded in `challengerEntries[].noPayoutIdx`.
  // Set up by `collectDepositorGraphPsbts` (payout pushed first, then each
  // nopayout). A future refactor that reorders the array would silently
  // extract the wrong signature for the wrong slot — Critical Path #3.
  // Payout and every NoPayout PSBT are signed on input 0 (depositor script-path).
  const DEPOSITOR_SIGNED_INPUT_INDEX = 0;

  assertPsbtUnsignedTxMatches(psbtPairs[0]);
  const payoutSignature = extractPayoutSignature(
    psbtPairs[0].returnedPsbtHex,
    depositorPubkey,
  );
  // Critical Path #7: verify the wallet's signature against a sighash recomputed
  // from the PSBT we built (psbtPairs[0].requestedPsbtHex), not the returned one.
  assertScriptPathSchnorrSignature({
    requestedPsbtHex: psbtPairs[0].requestedPsbtHex,
    signatureHex: payoutSignature,
    signerXOnlyPubkeyHex: depositorPubkey,
    inputIndex: DEPOSITOR_SIGNED_INPUT_INDEX,
  });

  const perChallenger: Record<string, DepositorPreSigsPerChallenger> = {};
  for (const entry of challengerEntries) {
    assertPsbtUnsignedTxMatches(psbtPairs[entry.noPayoutIdx]);
    const nopayoutSignature = extractPayoutSignature(
      psbtPairs[entry.noPayoutIdx].returnedPsbtHex,
      depositorPubkey,
    );
    assertScriptPathSchnorrSignature({
      requestedPsbtHex: psbtPairs[entry.noPayoutIdx].requestedPsbtHex,
      signatureHex: nopayoutSignature,
      signerXOnlyPubkeyHex: depositorPubkey,
      inputIndex: DEPOSITOR_SIGNED_INPUT_INDEX,
    });
    perChallenger[entry.challengerPubkey] = {
      nopayout_signature: nopayoutSignature,
    };
  }

  return {
    payout_signatures: {
      payout_signature: payoutSignature,
    },
    per_challenger: perChallenger,
  };
}

// ============================================================================
// Main entry point
// ============================================================================

/**
 * Authoritative inputs required to construct the depositor's Payout AND every
 * per-challenger NoPayout PSBT locally. Every field here must come from
 * trusted on-chain sources, not from the vault provider response. They feed
 * directly into the Taproot sighash.
 */
export interface DepositorGraphSigningContext {
  /**
   * Vault core (tx-graph) version the vault was registered under — the
   * vault's stamped on-chain `vaultCoreVersion` from `BTCVaultRegistry`.
   * Selects which graph's connector scripts every PSBT is rebuilt with.
   */
  vaultCoreVersion: number;
  /** Raw pegin BTC transaction hex (provides the depositor's signed prevout) */
  peginTxHex: string;
  /** Depositor's BTC public key (x-only, 64-char hex, no 0x prefix) */
  depositorBtcPubkey: string;
  /** Vault provider's BTC public key (x-only hex, no prefix) */
  vaultProviderBtcPubkey: string;
  /** Sorted vault keeper BTC public keys (x-only hex, no prefix) */
  vaultKeeperBtcPubkeys: string[];
  /** Sorted universal challenger BTC public keys (x-only hex, no prefix) */
  universalChallengerBtcPubkeys: string[];
  /** Pegin CSV timelock from the locked offchain params version (blocks) */
  timelockPegin: number;
  /**
   * Tx-graph fee rate (sat/vB) from the locked offchain params version —
   * bounds the depositor-claimer payout's implicit fee (payout fee band).
   */
  protocolFeeRate: bigint;
  /**
   * Assert CSV timelock from the locked offchain params version (blocks).
   * Sourced from the on-chain ProtocolParams contract via
   * `ViemProtocolParamsReader.getOffchainParamsByVersion(...).timelockAssert`.
   */
  timelockAssert: number;
  /**
   * Security council member x-only public keys (hex, no prefix). Sourced from
   * the on-chain ProtocolParams contract via
   * `ViemProtocolParamsReader.getOffchainParamsByVersion(...).securityCouncilKeys`.
   */
  councilMembers: string[];
  /**
   * M-of-N council quorum threshold. Sourced from the on-chain ProtocolParams
   * contract via `ViemProtocolParamsReader.getOffchainParamsByVersion(...).councilQuorum`.
   */
  councilQuorum: number;
  /** BTC network (Mainnet, Testnet, etc.) */
  network: Network;
  /**
   * On-chain registered depositor payout scriptPubKey (hex, with or without
   * 0x prefix). Used to assert the VP-advertised payout transaction pays to
   * the depositor's registered address before the wallet produces a signature.
   */
  registeredPayoutScriptPubKey: string;
  /**
   * RFC-006 operator payout destinations. Forwarded to `buildPayoutPsbt` for
   * shape completeness only: this graph is signed under the
   * `depositor-as-claimer` role, whose payout has two outputs and reads
   * neither the keeper map nor the VP commission destination.
   */
  vkClaimerPayoutScriptPubKeys: Readonly<Record<string, string>>;
  /** See {@link vkClaimerPayoutScriptPubKeys} — unused for this role. */
  vpCommissionScriptPubKey: string;
}

export interface SignDepositorGraphParams {
  /** The depositor graph from VP response */
  depositorGraph: DepositorGraphTransactions;
  /** Bitcoin wallet for signing */
  btcWallet: BitcoinWallet;
  /** Authoritative inputs used to rebuild every PSBT locally */
  signingContext: DepositorGraphSigningContext;
}

/**
 * Sign all depositor graph transactions and assemble into presignatures.
 *
 * Flow:
 * 1. Build payout + per-challenger nopayout PSBTs locally
 * 2. Batch sign via wallet.signPsbts() if available, else sequential signPsbt()
 * 3. Extract Schnorr signatures from each signed PSBT
 * 4. Assemble into DepositorAsClaimerPresignatures
 */
export async function signDepositorGraph(
  params: SignDepositorGraphParams,
): Promise<DepositorAsClaimerPresignatures> {
  const { depositorGraph, btcWallet, signingContext } = params;

  const walletPublicKey = await btcWallet.getPublicKeyHex();
  // Fail fast if the connected wallet doesn't match the on-chain registered
  // depositor key — otherwise extractPayoutSignature later fails after
  // multiple wallet popups with an opaque "no signature found" error.
  const { depositorPubkey } = validateWalletPubkey(
    walletPublicKey,
    stripHexPrefix(signingContext.depositorBtcPubkey),
  );

  // 1. Build all PSBTs locally
  const { psbtHexes, signOptions, challengerEntries } =
    await collectDepositorGraphPsbts(
      depositorGraph,
      walletPublicKey,
      signingContext,
    );

  // 2. Sign all PSBTs (batch when supported, sequential fallback for mobile)
  // signPsbtsWithFallback guarantees one signed PSBT per input (or throws), so
  // no separate arity check is needed here.
  const signedPsbtHexes = await signPsbtsWithFallback(
    btcWallet,
    psbtHexes,
    signOptions,
  );

  // 3. Pair requested with signed and extract signatures
  const psbtPairs: PsbtPair[] = psbtHexes.map((requestedPsbtHex, i) => ({
    requestedPsbtHex,
    returnedPsbtHex: signedPsbtHexes[i],
  }));
  return extractDepositorGraphSignatures(
    psbtPairs,
    challengerEntries,
    depositorPubkey,
  );
}
